/**
 * audio-enhance.js — night mode, dialogue boost and volume above 100% for a
 * playing <video>. Desktop players have had this for twenty years (VLC boosts
 * to 400%, MPC-HC normalises and remaps channels); no browser-based player
 * offers any of it.
 *
 * The three things people actually complain about watching a film at home:
 *   - explosions wake the house, whispers are inaudible  → 'night'
 *   - dialogue is buried under the music bed             → 'voice'
 *   - the whole file is just quiet                       → boost > 1
 *
 * Design notes that are NOT optional:
 *
 *  1. createMediaElementSource() may be called ONCE per element, and from that
 *     moment ALL audio flows through the graph. If the context is suspended or
 *     the graph is broken, the video goes silent. So the source is created
 *     LAZILY — only when the viewer actually turns something on — and never on
 *     attach. A tool that does nothing must not be able to mute the film.
 *  2. An AudioContext starts suspended without a user gesture. Every entry
 *     point resumes it and reports failure rather than silently muting.
 *  3. buildGraph() is pure and takes any BaseAudioContext, so the exact same
 *     chain can be run in an OfflineAudioContext and MEASURED
 *     (tests/js/audio-enhance.test.mjs) instead of eyeballed.
 */
(function () {
    'use strict';

    /* Presets are measured, not vibes — see the test for the numbers each of
       these is pinned to (peak reduction, quiet-passage lift, RMS change). */
    var PRESETS = {
        off: null,
        /* Even out loud/quiet without colouring: gentle ratio, slow release. */
        normalize: {
            compressor: { threshold: -24, knee: 12, ratio: 3, attack: 0.006, release: 0.28 },
            makeup: 1.6, bands: []
        },
        /* Night: hard on peaks, generous makeup, so the quiet stays audible at
           a low master volume and the loud never spikes. */
        night: {
            compressor: { threshold: -34, knee: 8, ratio: 12, attack: 0.003, release: 0.20 },
            makeup: 3.0, bands: [{ type: 'lowshelf', freq: 120, gain: -6 }]
        },
        /* Dialogue: lift the speech formant range, shave the music bed under it. */
        voice: {
            compressor: { threshold: -28, knee: 10, ratio: 4, attack: 0.004, release: 0.25 },
            makeup: 2.0,
            bands: [
                { type: 'highpass', freq: 90, gain: 0 },
                { type: 'peaking', freq: 2200, gain: 6, q: 0.9 },
                { type: 'lowshelf', freq: 180, gain: -4 }
            ]
        }
    };

    /* Build the chain on ANY BaseAudioContext and return {input, output}.
       Pure: no element, no globals — that is what makes it measurable. */
    function buildGraph(ctx, presetName, boost) {
        var preset = PRESETS[presetName] || null;
        var input = ctx.createGain();
        var node = input;

        if (preset) {
            (preset.bands || []).forEach(function (b) {
                var f = ctx.createBiquadFilter();
                f.type = b.type;
                f.frequency.value = b.freq;
                if (b.gain) f.gain.value = b.gain;
                if (b.q) f.Q.value = b.q;
                node.connect(f); node = f;
            });
            var comp = ctx.createDynamicsCompressor();
            var c = preset.compressor;
            comp.threshold.value = c.threshold;
            comp.knee.value = c.knee;
            comp.ratio.value = c.ratio;
            comp.attack.value = c.attack;
            comp.release.value = c.release;
            node.connect(comp); node = comp;
        }

        var out = ctx.createGain();
        out.gain.value = Math.max(0, (preset ? preset.makeup : 1) * (boost || 1));
        node.connect(out);
        node = out;

        /* Brickwall limiter — NOT decoration. Measured before it existed:
           'voice' peaked at 1.022 and boost=2 at 1.800, i.e. both clipped, and
           clipping is audible crackle, not loudness. Anything that applies gain
           gets limited; a true bypass (off + boost 1) stays untouched so the
           tool cannot colour audio nobody asked it to touch. */
        /* Ceiling. A WaveShaper is used ALONE here, deliberately:
           - a DynamicsCompressor is not a brickwall (measured overshoot to
             1.064 on an instant transient even at 1 ms attack), and
           - Chrome's DynamicsCompressor applies an implicit makeup gain even
             when the signal never crosses the threshold, so stacking one as a
             "safety" limiter turned a x2.0 boost into a measured x2.166.
           The shaper is exactly linear below 0.7 and asymptotes to 1.0, so the
           output cannot exceed full scale whatever gain precedes it, and a
           quiet file boosted 2x is boosted exactly 2x. */
        if (preset || (boost && boost !== 1)) {
            var shaper = ctx.createWaveShaper();
            shaper.curve = softClipCurve();
            shaper.oversample = '2x';
            node.connect(shaper); node = shaper;
        }
        return { input: input, output: node };
    }

    /* WaveShaper maps its curve across an input domain of EXACTLY -1..+1
       (out-of-range input clamps to the end entries). Building the table over
       any other range silently rescales everything: a first cut spanned -2..+2
       and doubled the whole signal — boost=2 measured as x4.3 — while still
       "limiting", so it looked like it worked. Keep this domain. */
    var _curve = null;
    function softClipCurve() {
        if (_curve) return _curve;
        var n = 4096, c = new Float32Array(n), KNEE = 0.7;
        for (var i = 0; i < n; i++) {
            var x = (i / (n - 1)) * 2 - 1;              // input range -1..+1
            var a = Math.abs(x), s = x < 0 ? -1 : 1;
            c[i] = a <= KNEE ? x : s * (KNEE + (1 - KNEE) * Math.tanh((a - KNEE) / (1 - KNEE)));
        }
        _curve = c;
        return c;
    }

    /* One capture per element, and one handle per element.
       createMediaElementSource() may run only ONCE on a given element — a
       second call throws InvalidStateError, and engage() swallowed that and
       returned false, i.e. the effect silently did nothing. That was
       unreachable while this module was the only caller; it stopped being true
       when audio-track-sidecar.js started asking for the same element to be
       SILENCED while the tool's night-mode/boost still owned it. Handing back
       the same handle makes "who captured it first" a non-question. */
    var _byEl = (typeof WeakMap === 'function') ? new WeakMap() : null;

    function attach(video) {
        if (_byEl && video) { var prev = _byEl.get(video); if (prev) return prev; }
        var handle = build(video);
        if (_byEl && video) { try { _byEl.set(video, handle); } catch (_) {} }
        return handle;
    }

    function build(video) {
        var ctx = null, source = null, graph = null;
        var mode = 'off', boost = 1, active = false, silent = false;

        function ensureCtx() {
            if (ctx) return ctx;
            var AC = window.AudioContext || window.webkitAudioContext;
            if (!AC) return null;
            try { ctx = new AC(); } catch (_) { return null; }
            return ctx;
        }

        /* Route element audio through the graph. Only ever called when the
           viewer asked for an effect. */
        function engage() {
            if (!ensureCtx()) return false;
            if (!source) {
                try { source = ctx.createMediaElementSource(video); }
                catch (_) { return false; }   // already captured elsewhere
            }
            rebuild();
            active = true;
            if (ctx.state === 'suspended') { try { ctx.resume(); } catch (_) {} }
            return true;
        }

        /* Last node before the speakers, so silence survives every rebuild and
           does not disturb the viewer's own mode/boost settings. */
        var mute = null;
        function ensureMute() {
            if (!mute && ctx) { mute = ctx.createGain(); mute.gain.value = silent ? 0 : 1; mute.connect(ctx.destination); }
            return mute;
        }

        function rebuild() {
            if (!source) return;
            try { source.disconnect(); } catch (_) {}
            if (graph) { try { graph.output.disconnect(); } catch (_) {} }
            graph = buildGraph(ctx, mode, boost);
            source.connect(graph.input);
            graph.output.connect(ensureMute() || ctx.destination);
        }

        return {
            /* 'off' | 'normalize' | 'night' | 'voice' */
            setMode: function (m) {
                if (!(m in PRESETS)) return false;
                mode = m;
                if (mode === 'off' && boost === 1 && !active) return true;
                return engage();
            },
            getMode: function () { return mode; },
            /* 1 = untouched, up to 3 = +300%. Above 1 the element's own volume
               control still works — this multiplies it. */
            setBoost: function (x) {
                boost = Math.max(0.1, Math.min(4, +x || 1));
                if (mode === 'off' && boost === 1 && !active) return true;
                return engage();
            },
            getBoost: function () { return boost; },
            isActive: function () { return active; },
            /* Silence the ELEMENT without touching .muted / .volume.
               Those two are the viewer's controls — video-player.js binds the
               mute button straight to `video.muted = !video.muted` — so a tool
               that needs the element quiet for its own reasons (the audio-track
               sidecar plays a different track over a muted picture) cannot
               borrow them: the next click on the volume UI would undo it, and
               the viewer's own mute would become a no-op toggle.
               Returns false when the element cannot be routed through WebAudio
               at all, so the caller can refuse rather than play two tracks at
               once. */
            setSilent: function (on) {
                silent = !!on;
                if (!silent && !active && !source) return true;   // never engaged: nothing to unmute
                if (!engage()) return false;
                var m = ensureMute();
                if (!m) return false;
                try { m.gain.value = silent ? 0 : 1; } catch (_) { return false; }
                return true;
            },
            isSilent: function () { return silent; },
            /* The last node before the speakers. Exposed so a guard can put an
               analyser on exactly what the viewer hears — asserting on a flag
               would only prove the flag was set. */
            outputNode: function () { return mute; },
            /* A context started before a gesture is suspended; call on click.
               ALWAYS returns a settled-safe Promise. iOS Safari REJECTS
               resume() when the call is not inside a user-gesture chain, and a
               try/catch does not catch a rejected promise — only a synchronous
               throw — so the bare call left an unhandled rejection (Sentry
               class 116880408). Routed through the project's __safeResume when
               it is present, with an equivalent local fallback. */
            resume: function () {
                if (!ctx || ctx.state !== 'suspended') return Promise.resolve(false);
                try {
                    if (typeof window.__safeResume === 'function') {
                        return Promise.resolve(window.__safeResume(ctx)).catch(function () { return false; });
                    }
                    var p = ctx.resume();
                    return (p && p.catch) ? p.catch(function () { return false; }) : Promise.resolve(true);
                } catch (_) { return Promise.resolve(false); }
            },
            state: function () { return ctx ? ctx.state : 'none'; },
            detach: function () {
                // Deliberately does NOT close the context: the element is
                // permanently captured once createMediaElementSource ran, so
                // tearing the graph down would leave it with nowhere to go.
                // Reconnect THROUGH the mute node, never straight to the
                // destination: dropping the effects must not also undo a
                // silence somebody else asked for.
                if (graph && ctx) { try { source.disconnect(); source.connect(ensureMute() || ctx.destination); } catch (_) {} }
                active = false; mode = 'off'; boost = 1;
            }
        };
    }

    window.AudioEnhance = {
        attach: attach,
        buildGraph: buildGraph,
        PRESETS: PRESETS,
        MODES: Object.keys(PRESETS)
    };
    if (typeof module !== 'undefined' && module.exports) {
        module.exports = window.AudioEnhance;
    }
})();
